import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, EventEmitter, OnChanges, SimpleChanges, Input, Output, SimpleChange, HostListener } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Observable, concat, merge, from, of, finalize, EMPTY, Subscription, Subject, tap, firstValueFrom, forkJoin } from 'rxjs';
import { map, switchMap, concatMap, last, debounceTime, distinctUntilChanged, filter, catchError } from 'rxjs/operators';

import { MenuItem, MessageService, FilterService } from 'primeng/api';
import { Table } from 'primeng/table';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';
import { round1, round2 } from 'src/app/shared/round';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Document, DocItems, DocumentStatusFlag } from 'src/app/shared/document';
import { Product, ProductInfo } from 'src/app/shared/product';
import { ProductApiService, StockResult } from 'src/app/services/product-api.service';
import { chunkArray } from 'src/app/shared/chunk-array';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { IncomePriceBreakdownDialogComponent } from 'src/app/visualcomponents/income-price-breakdown-dialog/income-price-breakdown-dialog.component';
import { ManufacturingBackdocPriceCheckDialogComponent } from 'src/app/visualcomponents/manufacturing-backdoc-price-check-dialog/manufacturing-backdoc-price-check-dialog.component';
import { ManufacturingBackdocRecompute } from 'src/app/shared/manufacturing-backdoc-recompute';

/**
 * Inline-editing table for purchase/return/receipt/internal-movement documents
 * (DOCTYPEs 2, 3, 4, 5).
 *
 * **Pipeline architecture:**
 * - `docItemList$` — debounced `switchMap(getDocItemsByDoc)` that reloads rows when the
 *   document ID changes; triggers `calculateDocItemsFields` enrichment per row.
 * - `calcDocSum$` — debounced (100 ms) Subject that aggregates `DOCSUM*` totals from
 *   `docItems`, then emits a `DocumentStatusFlag` via the `getDocumentStatus` output.
 *
 * **DOCTYPE-branched enrichment (`calculateDocItemsFields`):**
 * - Cases 4/5 (goods receipt / internal movement): `getProductStockBefore`,
 *   `getProductLcStockBefore`, `getProdIncomePriceById`, `getProdInfoById`.
 * - Cases 2/3 (purchase / return): `getProductStockAfter`, `getActcListById`,
 *   `getProdInfoById`.
 *
 * **Bulk price updates:** `updIncomePrice`, `updCurrPrice`, `updMainPrice` apply a single
 * price to all selected rows and persist via `putDocItemById`.
 *
 * Row editing uses the clone-shadow pattern: `docItemsCopy` holds originals and
 * `docItemsClone` mirrors edit-in-progress rows indexed by `cloneIndex`.
 *
 * Column visibility is persisted in `localStorage` under key
 * `edit-document-table-columns-4`.
 */
@Component({
  selector: 'app-edit-document-content-receipt',
  templateUrl: './edit-document-content-receipt.component.html',
  styleUrls: ['./edit-document-content-receipt.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentContentReceiptComponent implements OnInit, OnDestroy, OnChanges {

  private globalSettinsData: GlobalSettingsData = null;
  private docItemQuery: Subscription = null;
  private diSumQuery: Subscription = null;
  private diSaveQuery: Subscription = null;
  private diGroupQuery: Subscription = null;
  private _parentDocQuery: Subscription = null;

  private _productInfoQuery: Subscription = null;
  private _prodShippedQuery: Subscription = null;

  /** Debounced Subject that re-aggregates document totals and emits `getDocumentStatus`. */
  private calcDocSum$ = new Subject<Document>();
  private calcDocSumSubs: Subscription = null;

  /** Whether to render the table header toolbar. */
  @Input() showHeader: boolean = true;
  /** Whether to show add/edit/delete action controls. */
  @Input() allowEditControls: boolean = true;
  /** Whether to show payment-sum footer row. */
  @Input() showPaysum: boolean = true;

  /** Column header label for the income price field. */
  @Input() incomePriceTxt: string = 'Расчетная'; // Входная
  /** Column header label for the income currency price field. */
  @Input() incomeCurPriceTxt: string = 'Расчетная $'; // Входная $

  loading: boolean = false;

  typeDeleteConfirmation: boolean = false;
  confirmText: string = 'DELETE 12';
  deleteAllRows: boolean = false;

  selectedDocType: number = 0;

  sDI: DocItems[] = [];

  allDocItemsChanged: boolean = false;

  docItems: DocItems[] = [];

  docItemsCopy: DocItems[] = [];
  docItemsClone: DocItems[] = [];

  @ViewChild('docItemTable') docItemTable: Table;
  @ViewChild('priceBreakdownDialog') priceBreakdownDialog: IncomePriceBreakdownDialogComponent;
  @ViewChild('manufacturingBackdocDialog') manufacturingBackdocDialog: ManufacturingBackdocPriceCheckDialogComponent;

  /** Same threshold `ManufacturingBackdocPriceCheckDialogComponent` uses for its own
   *  `isMismatch` getter — kept identical so the `mbm` flag (escalated to `MinorIssue`
   *  below) agrees with what the dialog itself would show for the same row. */
  private readonly MANUFACTURING_MISMATCH_TOLERANCE = 0.5;

  /** The `DocItems` row `applyManufacturingBackdocCorrection` writes the recomputed price
   *  onto — set by `openManufacturingBackdocPriceCheck` right before opening the dialog,
   *  since the dialog itself holds no reference to the row it was opened for. */
  private _manufacturingBackdocTargetDi: DocItems = null;
  private _manufacturingBackdocQuery: Subscription = null;

  //@HostListener('keydown.space', ['$event'])
  //onSpaceKeyDown(event: KeyboardEvent) {
  //  event.stopPropagation();
  //  event.preventDefault();
  //  if (event.target) {
  //    event.target['value'] = event.target['value'] + ' ';
  //    (event.target as HTMLElement).focus();
  //  }
  //}

  editMode: boolean = false;
  confirmDeleteOn: boolean = false;
  canSaveRow: boolean = false;
  private docItemList$ = new Subject<number>();

  /** The document whose line items are being edited. */
  @Input() inputDocument: Document = null;

  /** Reserved trigger input — currently unused, kept for API compatibility. */
  @Input() knockKnock: boolean = false;

  private storeNameShowRowOptions: string = 'edit-document-table-columns-4';

  showDocRowsOptions = [
    { name: 'В валюте', value: 1 },
    { name: 'Без НДС', value: 2 },
    { name: 'УКТЗЕД', value: 4 },
    { name: 'Резерв', value: 8 },
    { name: 'Примечание', value: 16 },
    { name: 'Входная', value: 32 },
    { name: 'Доходность', value: 64 },
];

  showFilterOptions = [
    { name: 'Инвойс', icon: 'pi pi-filter', value: 1, disabled: false },
  ];

  showFilterValue: number[] = [1];

  public get showFilter(): boolean { return this.showFilterValue.includes(1); }

  allowOld: boolean = false;

  /** When true, rows with zero `DOCITEMQTY` are included in printed forms. */
  @Input() countEmpty: boolean = false;

  /** Overrides column-visibility bitmask from the caller; skips localStorage restore when set. */
  @Input() setShowRowOptions: number[];

  showRowOptions: number[] = [];

  public get noItems(): boolean { return ((this.docItems || []).length < 1); }

  public priceUp(di: DocItems): boolean { return (di && (di.DOCINCOMEPRICE <= di.DOCITEMPRICE)); }

  public getToolTips1(di: DocItems): string {
    let val = 0;

    if (di && (di.DOCITEMPRICE > 0)) {
      val = (((di.DOCITEMPRICE - di.DOCINCOMEPRICE) / di.DOCITEMPRICE)) * 100;
    }

    return formatNumber(val, 'uk', '1.0-1')+'%';
  }

  public curPriceUp(di: DocItems): boolean { return (di && ((di.DOCINCOMEPRICE / di.DOCITEMCRATE) <= di.DOCITEMVPRICE)); }

  public getToolTips2(di: DocItems): string {
    let val = 0;

    if (di && (di.DOCITEMVPRICE > 0)) {
      val = (((di.DOCITEMVPRICE - (di.DOCINCOMEPRICE / di.DOCITEMCRATE)) / di.DOCITEMVPRICE)) * 100;
    }

    return formatNumber(val, 'uk', '1.0-1')+'%';
  }

  public get seePriceArrow(): boolean { return [2,3].includes(this.inputDocument.DOCTYPE); }

  /*
   *  TODO: load access right to check is U4ETKA is visible to
   */
  public get canSeeU4etka(): boolean { return ([2,3,4,5].includes(this.inputDocument.DOCTYPE) && (this.showRowOptions.includes(32) || this.showRowOptions.includes(64))); }

  public get seeCurrency(): boolean { return this.showRowOptions.includes(1); }

  public get seeWoVAT(): boolean { return this.showRowOptions.includes(2); }

  public get seeTariffCode(): boolean { return this.showRowOptions.includes(4); }

  public get seeReservation(): boolean { return this.showRowOptions.includes(8); }

  public get seeDiNote(): boolean { return this.showRowOptions.includes(16); }

  public get seeProfit(): boolean { return ([4].includes(this.inputDocument.DOCTYPE) && this.showRowOptions.includes(64)); }

  public get seeShipped(): boolean { return [4].includes(this.inputDocument.DOCTYPE); }

  public get allowUpdIncome(): boolean { return [5].includes(this.inputDocument.DOCTYPE); }

  /** True when the currently open document is itself a manufacturing backward leg
   *  (DOCTYPE 5, created by `new-document-dlg-process-tran-prod`'s "Возврат из
   *  производства" flow) — the scope the recompute check/dialog applies to. Matches
   *  that component's own `DOCNOTE2` marker text exactly (see its `DOCNOTE2:` line). */
  public get isManufacturingBackDocument(): boolean {
    return this.inputDocument?.DOCTYPE === 5 && !!this.inputDocument?.DOCNOTE2?.startsWith('Возврат из производства');
  }

  public get selection(): boolean { return this.sDI ? true : false; }

  public get changes(): boolean { return this.editMode || this.allDocItemsChanged; }

  public get allowTopButtons(): boolean { return this.docItems && (this.docItems.length > 10); }

  public allowSave(di: DocItems): boolean { return di && (di.PRODID ? true : false) && this.canSaveRow; }

  cf3: any = {};

  excelExportItems: MenuItem[] = [
    {
      label: 'С логисткой', icon: 'pi pi-star-o', disabled: false, command: () => {
        this.onExportDataWithLogistic();
      }
    }
  ];

  contentReady: boolean = false;

  public get disableExportButton(): boolean { return this.editMode || !this.contentReady || this.noItems; }

  /** Emits `true` when entering edit mode, `false` when edit is saved or cancelled. */
  @Output() editing: EventEmitter<boolean> = new EventEmitter();

  // Initial value indicated serious issue in document items.
  // would clear during the calculation of document profit and emitted to
  // parent component

  documentItemsStatus: number = 101;

  /** Emits a `DocumentStatusFlag` integer after each `calcDocSum$` aggregation cycle. */
  @Output() getDocumentStatus: EventEmitter<number> = new EventEmitter();

  /** Two-way binding companion for `countEmpty`; emits when the user toggles zero-qty inclusion. */
  @Output() countEmptyChange: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
    private docApi: DocumentApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private filterService: FilterService,
  ) {
    // Below thread calculate document summary on every change of docItems array
    // in general it is fast and could be called every time when something changed

    // Also this thread check the overall status of document items and emit it to parent component

    this.calcDocSumSubs = this.calcDocSum$.pipe(

      debounceTime(100),
      filter(doc => (doc !== null)),

      concatMap(doc => {

        console.log('*** EditDocumentContentComponent calcDocSum$ start: ');

        doc.DOCITEMS = (this.docItems || []).filter(e => this.countEmpty || (e.DOCITEMQTY != 0)).length;
        doc.DOCTOTALITEMS = this.docItems.filter(e => this.countEmpty || (e.DOCITEMQTY != 0)).reduce((r, di) => r + di.DOCITEMQTY, 0);

        doc.DOCSUMU4ETKA = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.U4ETKA, 0);
        doc.DOCVSUM = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.DOCITEMVPRICE, 0);
        doc.DOCSUMWOVAT = this.docItems.reduce((r, di) => r + di.DOCITEMSUMWOVAT, 0);
        doc.DOCVAT = this.docItems.reduce((r, di) => r + (di.DOCITEMSUM - di.DOCITEMSUMWOVAT), 0);
        doc.DOCSUM = this.docItems.reduce((r, di) => r + di.DOCITEMSUM, 0);
        doc.DOCSUMPROFIT = this.docItems.reduce((r, di) => r + di.DOCITEMPROFITSUM, 0);

        this.documentItemsStatus = 0;

        return from(this.docItems).pipe(
          map(di => {

            /* doctypes.includes(1,12)
              рахунок фактура
              комерційна пропозиція
        
                  + (di.bip ? 'Цена ниже входной! ' : '')
                  + (di.bmd ? 'Скидка больше максимальной! ' : '')
                  + (di.cnm ? 'Валюта не соответствует установленной в документе! ' : '')
                  + (di.rnm ? 'Курс не соответствует установленному в документе! ' : '')
                  + (di.cpn ? 'Валютная цена не соответствует расчетной! ' : '')
                  + (di.mtc ? 'Не указан УКТЗЕД товара! ' : '')
                  + (di.nta ? 'Не подтвержден УКТЗЕД товара! ' : '')
                  + (di.lpf ? 'Возможный убыток от операции! ' : '')
                  + (di.npc ? 'Не установлена категория товара ' : '')
 
                  di.hlt = (di.bip || di.bmd || di.cnm || di.rnm || di.mtc || di.npc || di.nta || di.lpf || di.cpn);
 
                  di.hlt set to true means that some document item has some issue
 
                  Even single serious issue (bip, lpf) set documentItemsStatus to SeriousError
          */

            if (di.hlt) {

              if (di.bip || di.cnm || di.rnm || di.mtc || di.lpf || di.mbc) {
                this.documentItemsStatus = DocumentStatusFlag.SeriousError.value;
              }

              if (this.documentItemsStatus < DocumentStatusFlag.SeriousError.value) {

                if (di.cnm || di.rnm || di.mtc || di.lpf || di.mbm) {
                  this.documentItemsStatus = DocumentStatusFlag.MinorIssue.value;
                }
              }
            }
            environment.production || console.log('*** EditDocumentContentComponent in calcDocSum$: ' + JSON.stringify({ DI: di.DOCITEMID, HL: di.hlt }));
          }),

          finalize(() => {

            environment.production || console.log('*** EditDocumentContentComponent in calcDocSum$ finalize: ' + JSON.stringify({ DI: this.documentItemsStatus }));
            this.getDocumentStatus.emit(this.documentItemsStatus);
          })
        );
      }))
      .subscribe();

    this.calcDocSumSubs = this.calcDocSum$.pipe(

      debounceTime(100),
      filter(doc => (doc !== null)),
      map(doc => {

        doc.DOCITEMS = this.docItems.length;

        doc.DOCTOTALITEMS = this.docItems.reduce((r, di) => r + di.DOCITEMQTY, 0);
        doc.DOCSUMU4ETKA = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.U4ETKA, 0);
        doc.DOCVSUM = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.DOCITEMVPRICE, 0);
        doc.DOCSUMWOVAT = this.docItems.reduce((r, di) => r + di.DOCITEMSUMWOVAT, 0);
        doc.DOCVAT = this.docItems.reduce((r, di) => r + (di.DOCITEMSUM - di.DOCITEMSUMWOVAT), 0);
        doc.DOCSUM = this.docItems.reduce((r, di) => r + di.DOCITEMSUM, 0);
        doc.DOCSUMPROFIT = this.docItems.reduce((r, di) => r + di.DOCITEMPROFITSUM, 0);

      })).subscribe();

    this.docItemQuery =
      this.docItemList$.pipe(
        debounceTime(400),
        switchMap((docId: number) => {
          this.loading = true;
          console.log('*** EditDocumentContentReceiptComponent docItemList$ subscribing....: ' + docId)

          return this.docApi.getDocItemsByDoc(docId);

        })
      )
        .subscribe(data => {

          console.log('*** EditDocumentContentReceiptComponent docItemList$ subscription: ')
          //environment.production || console.table(data);

          this.calculateDocItemsFields(data);     // <= Raw docItems[] from database
          this.calculateDocumentSummary();        // <= Processed docItems[] with all calculated fields
          // <= Here we have also document summary calculated
          this.loading = false;
        });
  }

  reloadDocContent(event) {
    console.log('*** EditDocumentContentReceiptComponent reloadDocContent: ' + JSON.stringify(event))

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
    }
  }

  saveDiCopy() {

    this.docItemsCopy = [];
    /*
     * TODO: Review this code
     */
    this.docItems.forEach(e => this.docItemsCopy.push({ ...e }));
  }

  restoreDiCopy() {
    this.docItems = [];
    this.docItemsCopy.forEach(e => this.docItems.push({ ...e }));
  }

  calculateDocumentSummary() {
    this.calcDocSum$.next(this.inputDocument);
  }

  /**
   * Opens the FIFO income-price breakdown popup for one row — same five positional args
   * (`PRODID, DOCMANCLIID, DOCMANDEPID, DOCCURRID, DOCITEMQTY, DOCDATE`) this component
   * already passes to `getProdIncomePriceById` at every call site (e.g.
   * `calculateDocItemsFields`'s DOCTYPE 4/5 branches), so the breakdown reconstructs the
   * exact same price the cell shows.
   *
   * `DOCCURRID` (passed as `cr`) means the API's `ConvertedPrice`/weighted average come back
   * in the document's VALUE currency, matching `docItem.U4ETKA` — not `docItem.DOCINCOMEPRICE`,
   * which is already converted to main currency (`price[0].PRODINCOMEPRICE * doc.DOCRATEVAL`,
   * see `calculateDocItemsFields`/`updIncomePrice`). Passing `U4ETKA` as the comparison value
   * keeps the currencies matched; `DOCINCOMEPRICE` + `DOCRATEVAL` go along too so the popup can
   * show the main-currency comparison as well (found live 2026-09-03: comparing DOCINCOMEPRICE
   * against a value-currency breakdown flagged a false mismatch — 22 809,41 vs 483,25 on the
   * same, correct FIFO price, just two different currencies).
   *
   * DOCTYPE 5 and 2/3 only: also passes `docItem.DOCITEMVPRICE`/`DOCITEMPRICE` — the price
   * actually RECORDED on this line — as `storedPriceCurrency`/`storedPriceMain`, so the popup can
   * flag drift between that recorded price and the cell's own live-recomputed price. For a
   * transfer (DOCTYPE 5), cost is carried along, not marked up, so `calculateDocItemsFields`'s
   * DOCTYPE-5 branch already treats "recorded price should equal income price" as the working
   * assumption (`DOCITEMPROFIT = DOCITEMPRICE - DOCINCOMEPRICE`) — found live 2026-09-03: a
   * transfer recorded at 647,99 no longer matched an 826,50 FIFO average once later receipts
   * shifted the batch layers, and neither existing comparison above could catch it, since
   * `U4ETKA`/`DOCINCOMEPRICE` are themselves recomputed live on every load (so they always agree
   * with this popup's own recomputation, near-tautologically) rather than read from what was
   * actually recorded. For a receipt (DOCTYPE 2/3), the recorded price literally *is* the cost
   * basis, and `calculateDocItemsFields`'s DOCTYPE-2/3 branch sets `U4ETKA`/`DOCINCOMEPRICE` from
   * the product's live `PRODINPRICE` rather than this line's own recorded price, so the same drift
   * is possible there too (added per owner request, 2026-09-03, after confirming DOCTYPE 5's fix).
   * Omitted for DOCTYPE 4: there is no stored/recorded income price to compare against at all
   * there — `DOCITEMPRICE` is the sale price, and profit/margin (`DOCITEMPROFIT`) are derived
   * on-the-fly from that sale price against the live `DOCINCOMEPRICE`, never the other way
   * around, so nothing analogous to `storedPriceCurrency`/`storedPriceMain` exists for this
   * DOCTYPE. The popup's job there is exactly what the two comparisons above already do — show
   * where the live income-price value came from, nothing more (owner confirmed, 2026-09-03).
   */
  openIncomePriceBreakdown(docItem: DocItems): void {
    // Manufacturing backward-leg + complex/BOM product: there is no FIFO receipt
    // history to show at all (the product was never purchased-in, it was produced),
    // which is exactly why this cell reads 0,00 and the FIFO popup says "no receipt
    // history" — that's not a data gap, it's the wrong data source for this row. The
    // real cost basis is the matched forward (production) legs, which is what the
    // manufacturing recompute dialog already shows. Route there instead of opening
    // the FIFO popup on a row it can never answer for.
    if (this.isManufacturingBackDocument && docItem.PRODKIND === 1) {
      this.openManufacturingBackdocPriceCheck(docItem);
      return;
    }

    const doc = this.inputDocument;
    const hasComparableStoredPrice = [2, 3, 5].includes(docItem.DOCTYPE);
    this.priceBreakdownDialog.open(
      docItem.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID,
      docItem.DOCITEMQTY, doc.DOCDATE,
      docItem.U4ETKA ?? null, doc.DOCRATEVAL ?? null, docItem.DOCINCOMEPRICE ?? null,
      hasComparableStoredPrice ? (docItem.DOCITEMVPRICE ?? null) : null,
      hasComparableStoredPrice ? (docItem.DOCITEMPRICE ?? null) : null,
    );
  }

  /**
   * Opens the manufacturing backdoc price-check popup for one backward-leg row.
   * Unlike `openIncomePriceBreakdown`, the dialog's `open()` takes no `DocItems`
   * reference at all — it only needs the backward-leg document's own `DOCID`
   * (`ufn_getmanufacturingbackdoc_recompute_iTVF` keys entirely off the back
   * document, not the row) — so the target row is remembered here instead, for
   * `applyManufacturingBackdocCorrection` to write back to once confirmed.
   */
  openManufacturingBackdocPriceCheck(di: DocItems): void {
    this._manufacturingBackdocTargetDi = di;
    this.manufacturingBackdocDialog.open(this.inputDocument.DOCID);
  }

  /**
   * Handles the dialog's `applyCorrection` output. Writes the recomputed currency
   * price onto the remembered target row and routes it through the existing
   * `onDOCITEMVPRICEChange` handler — the same path a manual edit of that cell
   * takes — so `DOCITEMPRICEWOVAT`/`DOCITEMPRICE` and the row's summary/flags all
   * recompute consistently. There is deliberately no separate write call here.
   */
  applyManufacturingBackdocCorrection(recompute: ManufacturingBackdocRecompute): void {
    const di = this._manufacturingBackdocTargetDi;
    if (!di) {
      return;
    }
    di.DOCITEMVPRICE = recompute.RecomputedUnitPriceCurrency;
    this.onDOCITEMVPRICEChange(null, di);
  }

  calculateFastRowSummary(di: DocItems) {
    //    console.log('*** EditDocumentContentComponent calculateFastRowSummary: ' + di.DOCITEMID);

    di.DOCITEMSUMWOVAT = di.DOCITEMQTY * di.DOCITEMPRICEWOVAT;
    di.DOCITEMSUM = di.DOCITEMQTY * di.DOCITEMPRICE;
    di.DOCITEMVSUM = di.DOCITEMQTY * di.DOCITEMVPRICE;
    di.DOCITEMSUMU4 = di.DOCITEMQTY * di.U4ETKA;
  }

/**
   * Computes per-row flags and calls `_parentDocQuery` to check parent-doc price parity (`pdp`).
   * DOCTYPE 4 sets `pdp` when the row price differs from the linked primary document.
   * Sets highlight flag `hlt` when any warning flag is active.
   */
  calculateRowSummary(di: DocItems): DocItems {
    //console.log('*** EditDocumentContentReceiptComponent calculateRowSummary: ' + di.DOCITEMID);

    switch (di.DOCTYPE) {

      case 2:
      case 3:
        di.U4ETKA = di.prodInfo.PRODINPRICE;
        di.DOCINCOMEPRICE = di.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL;

      case 5:
        di.pdp = false;
        di.mbc = false;
        di.mbm = false;

        // Manufacturing backdoc recompute check — backward-leg (this document) +
        // complex/BOM product (PRODKIND=1) rows only. Runs on every load
        // (recompute-on-open, per docs/PROMPT_manufacturing-backdoc-price-check-dialog.md's
        // resolved DOCFLAG design), not just on demand from the dialog button.
        //
        // Also the ONLY correct source of an income price for these rows, and
        // overrides the placeholder `getProdIncomePriceById` already wrote in
        // `calculateDocItemsFields`'s DOCTYPE-5 branch: a manufactured product was
        // never purchased in via FIFO batches, so that call always comes back
        // empty/zero here (found live 2026-09-05, document 127128 — the cell showed
        // 0,00 for exactly this reason). `row` existing at all (even unconfirmed)
        // is a better number than a hard zero — the row's own `mbc`/`hlt` highlight
        // already flags an unconfirmed match for review, so this doesn't paper over
        // anything.
        //
        // Its own subscribe recomputes `di.hlt` from every flag's CURRENT value
        // (not just OR-ing mbc/mbm in) because it can resolve either before or
        // after the shared `_parentDocQuery` block below, which otherwise would
        // overwrite `di.hlt` and silently drop this check's contribution.
        if (this.isManufacturingBackDocument && di.PRODKIND === 1) {
          this._manufacturingBackdocQuery =
            this.docApi.getManufacturingBackdocRecompute(this.inputDocument.DOCID).subscribe({
              next: (rows) => {
                const row = (rows || [])[0];
                di.mbc = !row || !row.IsFullyConfirmed;
                di.mbm = !!row && row.IsFullyConfirmed
                  && Math.abs(row.RecomputedUnitPriceMain - row.StoredUnitPriceMain) > this.MANUFACTURING_MISMATCH_TOLERANCE;

                if (row) {
                  di.DOCINCOMEPRICE = row.RecomputedUnitPriceMain;
                  di.U4ETKA = row.RecomputedUnitPriceCurrency;
                  di.DOCITEMPROFIT = di.DOCITEMPRICE - (di.DOCINCOMEPRICE ?? 0);
                  di.DOCITEMPROFITSUM = di.DOCITEMQTY * di.DOCITEMPROFIT;
                }

                di.hlt = (di.cnm || di.rnm || di.cpn || di.mtc || di.pdp || di.mbc || di.mbm
                  || ([4, 5].includes(di.DOCTYPE) && (di.ngs || di.nls))
                  || ([2, 3].includes(di.DOCTYPE) && (di.nta)));

                this.calculateDocumentSummary();
              },
              // A failed recompute leaves mbc/mbm false and the price fields at
              // whatever getProdIncomePriceById already set, rather than blocking
              // document load — this check is additive, not a load precondition.
              error: () => { },
            });
        }

      case 4:
        // TODO: pdp - Primary document Price does not match
        // pdp ?: boolean;
        // Primary document Price does not match
        this._parentDocQuery =
          of(this.inputDocument.PRIMDOCID).pipe(
            concatMap(docid => this.docApi.getDocItemsByDoc(docid).pipe(map(docitems => (di.DOCITEMPRICE != (docitems.find(r => r.PRODID == di.PRODID) || { DOCITEMPRICE: 0 }).DOCITEMPRICE)))),
            concatMap(val => of(val).pipe(map(pdp => {

              di.pdp = [4].includes(di.DOCTYPE) && pdp;

              //di.mst = di.prodInfo && ((di.DOCITEMQTY - di.DOCITEMSHIPPED) > di.prodInfo.PRODSTOCK);
              //di.sha = (di.DOCITEMSHIPPED == di.DOCITEMQTY);

              this.calculateFastRowSummary(di);

              di.cnm = this.inputDocument && (di.DOCITEMCURRID != this.inputDocument.DOCCURRID);
              di.rnm = this.inputDocument && (di.DOCITEMCRATE != this.inputDocument.DOCRATEVAL);
              di.cpn = round1(di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL) != round1(di.DOCITEMPRICE);

              di.mtc = ((di.PRODCUSTCODE || '') == '');

              di.ngs = (di.DOCITEMGLOBALSTOCK ?? 0) < di.DOCITEMQTY;
              di.nls = (di.DOCITEMLOCALSTOCK ?? 0) < di.DOCITEMQTY;

              di.hlt = (di.cnm || di.rnm || di.cpn || di.mtc || di.pdp || di.mbc || di.mbm || ([4, 5].includes(di.DOCTYPE) && (di.ngs || di.nls)) || ([2, 3].includes(di.DOCTYPE) && (di.nta)));

              this.calculateDocumentSummary();

            }))),
          )
            .subscribe();
        break;
    }
    return di;
  }

  /**
   * Enriches raw `DocItems[]` from the API with stock, price, and product-info fields.
   * DOCTYPE determines which API calls are made (see class-level JSDoc for per-case detail).
   */
  calculateDocItemsFields(data: DocItems[]) {
    //console.log('*** EditDocumentContentReceiptComponent calculateDocItemsFields')
    this.docItems = data;
    let doc = this.inputDocument;
    this.contentReady = false;

    switch (this.inputDocument.DOCTYPE) {

      case 4:
        this.diSumQuery = from(this.docItems).pipe(
          concatMap(di => {
            di.cloneIndex = 0;
            di.PRODVAT = (di.PRODVAT ?? this.globalSettinsData.vatValue);
            di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));

            return of(di);
          }),

          concatMap(di => this.prodApi.getProductStockBefore(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCDATE).pipe(
            map(stock => {
              di.DOCITEMGLOBALSTOCK = (stock as StockResult[])[0]?.stock ?? 0;
              return di;
            }),
            catchError(this.handleHttpError('getProductStockBefore', di))
          )),

          concatMap(di => this.prodApi.getProductLcStockBefore(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCLCOCLIID, doc.DOCDATE).pipe(
            map(stock => {
              di.DOCITEMLOCALSTOCK = (stock as StockResult[])[0]?.stock ?? 0;
              return di;
            }),
            catchError(this.handleHttpError('getProductLcStockBefore', di))
          )),

          concatMap(di => this.prodApi.getProdIncomePriceById(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID, di.DOCITEMQTY, doc.DOCDATE).pipe(
            map(price => {
              di.DOCINCOMEPRICE = price[0].PRODINCOMEPRICE * doc.DOCRATEVAL;

              // All calculations are in main currency!

              di.DOCITEMPROFIT = di.DOCITEMPRICE - (di.DOCINCOMEPRICE ?? 0);
              di.DOCITEMPROFITSUM = di.DOCITEMQTY * di.DOCITEMPROFIT;

              di.U4ETKA = price[0].PRODINCOMEPRICE;
              return di;
            }),
            catchError(this.handleHttpError('getProdIncomePriceById', di))
          )),

          concatMap(di => this.prodApi.getProdInfoById(di.PRODID, doc.DOCMANCLIID, doc.DOCCURRID,
            this.globalSettinsData.mainStockId,
            this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
            this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
            new Date())
            .pipe(
              map(data => {
                di.prodInfo = (data && data.length > 0) ? data[0] : null;
                this.calculateRowSummary(di);
                //console.log(di);
              }),
              catchError(this.handleHttpError('getProdInfoById'))
            )),

          finalize(() => {
            this.contentReady = true;
            this.inputDocument.items = this.docItems;
            this.saveDiCopy();
          }))
        .subscribe();
        break;

      case 5:
        this.diSumQuery = from(this.docItems).pipe(

          concatMap(di => {

            di.cloneIndex = 0;
            di.PRODVAT = (di.PRODVAT ?? this.globalSettinsData.vatValue);
            di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));

            return of(di);
          }),

          concatMap(di => this.prodApi.getProductStockBefore(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCDATE).pipe(
            map(stock => {
              di.DOCITEMGLOBALSTOCK = (stock as StockResult[])[0]?.stock ?? 0;
              return di;
            }),
            catchError(this.handleHttpError('getProductStockBefore', di))
          )),

          concatMap(di => this.prodApi.getProductLcStockBefore(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCLCOCLIID, doc.DOCDATE).pipe(
            map(stock => {
              di.DOCITEMLOCALSTOCK = (stock as StockResult[])[0]?.stock ?? 0;
              return di;
            }),
            catchError(this.handleHttpError('getProductLcStockBefore', di))
          )),

          concatMap(di => this.prodApi.getProdIncomePriceById(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID, di.DOCITEMQTY, doc.DOCDATE).pipe(
            map(price => {
              di.DOCINCOMEPRICE = (price[0].PRODINCOMEPRICE ?? di.prodInfo.PRODINPRICE) * doc.DOCRATEVAL;

              // All calculations are in main currency!

              di.DOCITEMPROFIT = (di.DOCITEMPRICE) - (di.DOCINCOMEPRICE ?? 0);
              di.DOCITEMPROFITSUM = di.DOCITEMQTY * di.DOCITEMPROFIT;
              di.U4ETKA = price[0].PRODINCOMEPRICE;

              return di;
            }),
            catchError(this.handleHttpError('getProdIncomePriceById', di))
          )),

          concatMap(di => this.prodApi.getProdInfoById(di.PRODID, doc.DOCMANCLIID, doc.DOCCURRID,
            this.globalSettinsData.mainStockId,
            this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
            this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
            new Date())
            .pipe(
              map(data => {
                di.prodInfo = (data && data.length > 0) ? data[0] : null;
                this.calculateRowSummary(di);
              }),
              catchError(this.handleHttpError('getProdInfoById'))
            )),

          finalize(() => {
            this.inputDocument.items = this.docItems;
            this.saveDiCopy();
            this.contentReady = true;
          }))

        .subscribe();
        break;

      case 2:
      case 3:
        this.diSumQuery = from(this.docItems).pipe(
          concatMap(di => {

            di.cloneIndex = 0;
            di.PRODVAT = (di.PRODVAT ?? this.globalSettinsData.vatValue);
            di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));

            return of(di);
          }),
          tap(di => environment.production || console.table(doc)),
          concatMap(di => this.prodApi.getProductStockAfter(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCDATE).pipe(
            map(stock => {
              di.DOCITEMGLOBALSTOCK = (stock as StockResult[])[0]?.stock ?? 0;
              return di;
            }),
            catchError(this.handleHttpError('getProductStockAfter', di))
          )),

          concatMap(di => this.prodApi.getProductLcStockAfter(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCLCOCLIID, doc.DOCDATE).pipe(
            map(stock => {
              di.DOCITEMLOCALSTOCK = (stock as StockResult[])[0]?.stock ?? 0;
              return di;
            }),
            catchError(this.handleHttpError('getProductLcStockAfter', di))
          )),

          concatMap(di => this.prodApi.getActcListById(di.PRODID).pipe(
            map(actc => {
              di.nta = true;
              if ((actc || []).length > 0) {
                di.nta = !(di.PRODCUSTCODE == actc[0].ACTC_STRING);
              }
              return di;
            }),
            catchError(this.handleHttpError('getActcListById', di))
          )),

          concatMap(di => this.prodApi.getProdInfoById(di.PRODID, doc.DOCMANCLIID, doc.DOCCURRID,
            this.globalSettinsData.mainStockId,
            this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
            this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
            new Date())
            .pipe(
              map(data => {
                di.prodInfo = data[0];
                this.calculateRowSummary(di);
              }),
              catchError(this.handleHttpError('getProdInfoById'))
            )),

          finalize(() => {
            this.inputDocument.items = this.docItems;
            this.saveDiCopy();
            this.contentReady = true;
          }))

        .subscribe();
        break;
    }
  }

  /** Persists column visibility to localStorage and unsubscribes all active subscriptions. */
  private handleHttpError<T>(operation: string, result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      const detail = error?.status === 0 ? `Немає з'єднання з сервером`
        : error?.status >= 500 ? 'Помилка сервера'
        : error?.message || 'Невідома помилка';
      this.messageService.add({ severity: 'error', summary: 'Помилка завантаження', detail, life: 5000 });
      return result !== undefined ? of(result) : (EMPTY as Observable<T>);
    };
  }

  ngOnDestroy(): void {
    console.log('*** EditDocumentContentReceiptComponent ngOnDestroy: ')

    if (!this.setShowRowOptions) {
      localStorage.setItem(this.storeNameShowRowOptions, JSON.stringify(this.showRowOptions));
    }
    localStorage.setItem(this.storeNameShowRowOptions + '-columns-filter-state', JSON.stringify(this.showFilterValue));

    if (this.docItemQuery) { this.docItemQuery.unsubscribe(); }
    if (this.calcDocSumSubs) { this.calcDocSumSubs.unsubscribe(); }
    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }
    if (this._parentDocQuery) { this._parentDocQuery.unsubscribe(); }
    if (this._manufacturingBackdocQuery) { this._manufacturingBackdocQuery.unsubscribe(); }
    if (this._prodShippedQuery) { this._prodShippedQuery.unsubscribe(); }
    if (this.diSaveQuery) { this.diSaveQuery.unsubscribe(); }
    if (this.diSumQuery) { this.diSumQuery.unsubscribe(); }
    if (this.diGroupQuery) { this.diGroupQuery.unsubscribe(); }
    if (this.calcDocSumSubs) { this.calcDocSumSubs.unsubscribe(); }
  }

  /**
   * Reacts to `inputDocument`, `knockKnock`, and `setShowRowOptions` changes.
   * - `inputDocument`: pushes the document ID onto `docItemList$` and adjusts price-column labels per DOCTYPE.
   * - `knockKnock`: force-reloads rows for the current document.
   * - `setShowRowOptions`: overrides the column visibility bitmask immediately.
   */
  ngOnChanges(changes: SimpleChanges): void {
    //    console.log('*** EditDocumentContentReceiptComponent ngOnDestroy: ')
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentContentReceiptComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //      console.log('*** EditDocumentContentReceiptComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'setShowRowOptions':
          this.showRowOptions = [...change.currentValue];
          break;

        case 'knockKnock':
          this.docItemList$.next(this.inputDocument.DOCID);
          break;

        case 'inputDocument':
          console.log('*** EditDocumentContentReceiptComponent ngOnChanges change.currentValue.DOCTYPE: ' + JSON.stringify(change.currentValue.DOCTYPE));
          if (change.currentValue) {
            this.docItemList$.next(change.currentValue.DOCID);

            console.log('*** EditDocumentContentReceiptComponent ngOnChanges change.currentValue.DOCTYPE: ' + JSON.stringify(change.currentValue.DOCTYPE));

            this.incomePriceTxt = (change.currentValue.DOCTYPE == 2) ? 'Расчетная' : 'Входная';
            this.incomeCurPriceTxt = (change.currentValue.DOCTYPE == 2) ? 'Расчетная $' : 'Входная $';
          }
          break;
      }
    }
  }

  /** Registers custom PrimeNG filter strategies, restores column state from localStorage, and wires up `docItemList$` and `calcDocSum$` pipelines. */
  ngOnInit(): void {
    console.log('*** EditDocumentContentReceiptComponent ngOnInit: ')

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    )
    );

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    if (!this.setShowRowOptions) {
      let _storedShowRowOptions: string = localStorage.getItem(this.storeNameShowRowOptions);
      if (_storedShowRowOptions) { this.showRowOptions = JSON.parse(_storedShowRowOptions); }
    }
    let _storedFilterOptions: string = localStorage.getItem(this.storeNameShowRowOptions + '-columns-filter-state');
    if (_storedFilterOptions) { this.showFilterValue = JSON.parse(_storedFilterOptions); }

    this.reloadDocContent(null);
  }

  onRowsOptionChange(event) {
    //  console.log('*** EditDocumentContentReceiptComponent in doChangeConditions: ' + JSON.stringify(event));
    //  console.log('*** EditDocumentContentReceiptComponent in doChangeConditions (showRowOptions): ' + JSON.stringify(this.showRowOptions));
  }

  onProductSelection(event: Product, di: DocItems) {
    console.log('*** EditDocumentContentReceiptComponent onProductSelection: ' + JSON.stringify(event));
    di.PRODID = event.PRODID;
    di.PRODCODE = event.PRODCODE;
    di.PRODVAT = event.PRODVAT;

    let doc = this.inputDocument;
    this.canSaveRow = false;

    this._prodShippedQuery =
      of(di)
      .pipe(
//        switchMap(di => this.prodApi.getProdIncomePriceById(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID, di.DOCITEMQTY, doc.DOCDATE)

        concatMap(di1 => this.prodApi.getProdIncomePriceById(di1.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID, di1.DOCITEMQTY, doc.DOCDATE).pipe(
          map(price => {
            console.log(price);
            di1.U4ETKA = price[0].PRODINCOMEPRICE;
            return di1;
          }),
          catchError(this.handleHttpError('getProdIncomePriceById', di1))
        )),

        concatMap(di2 => this.prodApi.getProdInfoById(di2.PRODID, doc.DOCMANCLIID, doc.DOCCURRID, doc.DOCMANDEPID, 0, 0, new Date()).pipe(
          map(data => {
            di2.prodInfo = data[0];

            di2.DOCITEMPRODNAME = event.PRODNAME.trim();

            di2.PRODVAT = (di2.PRODVAT ?? this.globalSettinsData.vatValue);
            let kVat = ((100.0 + di2.PRODVAT) / 100.0);

            switch (di2.DOCTYPE) {
              case 2:
              case 3:
                di2.DOCITEMVPRICE = di2.prodInfo.PRODINPRICE;
                di2.DOCITEMPRICE = di2.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL;

                di2.U4ETKA = di2.prodInfo.PRODINPRICE;
                di2.DOCINCOMEPRICE = di2.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL;

                di2.DOCITEMPRICEWOVAT = round2(di2.DOCITEMPRICE / kVat);
                break;

              case 4:
                /*
                 * Here we need price in main currency from parent invoice
                 */

                di2.DOCITEMVPRICE = di2.prodInfo.PRODPRICE;
                di2.DOCITEMPRICE = di2.prodInfo.PRODPRICE * this.inputDocument.DOCRATEVAL;
                di2.DOCITEMPRICEWOVAT = round2(di2.DOCITEMPRICE / kVat);
                break;

              case 5:
                /*
                 * TODO: Calculate actual income price based on quantity and stock id
                 */
                di2.DOCITEMVPRICE = di2.U4ETKA;
                di2.DOCITEMPRICE = di2.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL;
                di2.DOCITEMPRICEWOVAT = round2(di2.DOCITEMPRICE / kVat);
                break;

              default:
                di2.DOCITEMVPRICE = di2.prodInfo.PRODINPRICE;
                di2.DOCITEMPRICE = di2.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL;
                di2.DOCITEMPRICEWOVAT = round2(di2.DOCITEMPRICE / kVat);
                break;
            }
            return di2;
          }),
          catchError(this.handleHttpError('getProdInfoById-selection', di2))
        )),

        concatMap(di3 => this.docApi.getDocItemsByDoc(this.inputDocument.PRIMDOCID).pipe(
          map(docitems => {
            if (di3.DOCTYPE == 4) {
              let row = docitems.find(r => r.PRODID == di3.PRODID);
              if (row) {
                di3.DOCITEMVPRICE = row.DOCITEMPRICE / this.inputDocument.DOCRATEVAL;
                di3.DOCITEMPRICE = row.DOCITEMPRICE;
                di3.DOCITEMPRICEWOVAT = round2(di3.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
              }
            }
            return di3;
          }),
          catchError(this.handleHttpError('getDocItemsByDoc', di3 as any))
        )),

        concatMap(di4 => {

          this.calculateRowSummary(di4);
          this.canSaveRow = true;

          return of(di4);
        }),
      )
      .subscribe();
  }

  onAddRow(event) {
    console.log('*** EditDocumentContentReceiptComponent in onAddRow: ' + JSON.stringify(event));

    let maxRowIndex = 0;

    this.docItems.forEach(pp => { maxRowIndex = Math.max(maxRowIndex, pp.PAYITEMCLIID) });

    let newDi: DocItems = {

      DOCITEMID: -2,
      DOCID: this.inputDocument.DOCID,
      DOCTYPE: this.inputDocument.DOCTYPE,
      PRODID: null,
      DOCITEMPRODNAME: '',
      DOCITEMPRICE: 0,
      DOCITEMQTY: 1,
      DOCITEMVPRICE: 0,
      DOCITEMCURRID: this.inputDocument.DOCCURRID,
      DOCITEMCRATE: this.inputDocument.DOCRATEVAL,
      PAYITEMCLIID: maxRowIndex + 10,
      PAYITEMDEPID: null,
      DOCITEMTEXT1: '',
      DOCITEMTEXT2: 'Нет',
      CURCODE: '',
      U4ETKA: null,
      PRODVAT: this.globalSettinsData.vatValue,
      cloneIndex: 0
    };

    this.docItems = [...this.docItems, newDi];

    let ppe: DocItems = this.docItems.find(e => e.DOCITEMID == -2);
    this.onDiRowEditInit(ppe);
    this.docItemTable.initRowEdit(ppe);
  }

  onDiRowEditInit(esg: DocItems) {
    console.log('*** EditDocumentContentReceiptComponent in onPpRowEditInit: entry');
    //environment.production || console.table(esg);

    let newCloneIndex: number = 0;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [securityGroupsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.docItems.forEach(pp => { newCloneIndex = Math.max(newCloneIndex, pp.cloneIndex) });

    console.log('*** EditDocumentContentReceiptComponent in onPpRowEditInit: newCloneIndex is: ' + newCloneIndex);

    esg.cloneIndex = ++newCloneIndex;
    this.docItemsClone.push({ ...esg });

    esg.DOCITEMCURRID = this.inputDocument.DOCCURRID;
    esg.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;

    this.editMode = true;
    this.canSaveRow = true;
    this.editing.emit(this.editMode);
  }

  onDiRowEditCancel(esg: DocItems, index: number) {
    console.log('*** EditDocumentContentReceiptComponent in onPpRowEditCancel: ' + index);

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    if (esg.DOCITEMID == -2) {
      this.docItems.splice(index, 1);

    } else {
      this.docItems[index] = { ...this.docItemsClone[ppid] };
      this.docItems[index].cloneIndex = 0;
    }

    this.docItemsClone.splice(ppid, 1);

    this.editMode = false;
    this.editing.emit(this.editMode);
}

  onDiRowEditSave(esg: DocItems) {
    console.log('*** EditDocumentContentReceiptComponent in onDiRowEditSave: ');

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);
    esg.DOCITEMVPRICE = esg.DOCITEMPRICE / esg.DOCITEMCRATE;

    this.diSaveQuery =
      concat(((esg.DOCITEMID == -2) ? this.docApi.postDocItemById(esg) : this.docApi.putDocItemById(esg))
        .pipe(map(count => {
          console.log('*** EditDocumentContentReceiptComponent in onSubscribe to Save: ' + count);

          this.docItemsClone.splice(ppid, 1);
          esg.cloneIndex = 0;
          this.reloadDocContent(null);
          this.editMode = false;
          this.editing.emit(this.editMode);
        })))
        .subscribe();
  }

  onDOCITEMQTYChange(event, di: DocItems) {
    console.log('*** EditDocumentContentReceiptComponent onDOCITEMQTYChange: ' + JSON.stringify(event));
    let doc = this.inputDocument;

    switch (di.DOCTYPE) {
      case 5:
        /*
         * TODO: Calculate actual income price based on quantity and stock id
         */
        di.U4ETKA = 0;
        di.DOCITEMVPRICE = 0;
        di.DOCITEMPRICE = 0;
        di.DOCITEMPRICEWOVAT = 0;
        this.calculateRowSummary(di);
        this.canSaveRow = false;

        this._prodShippedQuery =
          of(di)
          .pipe(
            switchMap(di => this.prodApi.getProdIncomePriceById(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID, di.DOCITEMQTY, doc.DOCDATE)
              .pipe(map(price => {
                di.DOCITEMVPRICE = price[0].PRODINCOMEPRICE;
                di.PRODVAT = (di.PRODVAT ?? this.globalSettinsData.vatValue);
                di.DOCITEMPRICE = di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL;
                di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));

                this.calculateRowSummary(di);
                this.canSaveRow = true;
              }))))
            .subscribe();
        break;
    }
  }

  onDOCITEMVPRICEChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentReceiptComponent in onDOCITEMVPRICEChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentReceiptComponent in onDOCITEMQTYChange: ' + JSON.stringify(di));

    di.DOCITEMPRICEWOVAT = round2((di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL) / ((100.0 + di.PRODVAT) / 100.0));
    di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * ((100.0 + di.PRODVAT) / 100.0));

    this.calculateRowSummary(di);
  }

  onDOCITEMPRICEWOVATChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentReceiptComponent in onDOCITEMPRICEWOVATChange: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentReceiptComponent in onDOCITEMPRICEWOVATChange: ' + JSON.stringify(di));

    di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT) * ((100.0 + di.PRODVAT) / 100.0);
    di.DOCITEMVPRICE = di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL;
    di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));

    this.calculateRowSummary(di);
  }

  onDOCITEMPRICEChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentReceiptComponent in onDOCITEMPRICEChange: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentReceiptComponent in onDOCITEMPRICEChange: ' + JSON.stringify(di));

    di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
    di.DOCITEMVPRICE = di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL;
    di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * ((100.0 + di.PRODVAT) / 100.0));

    this.calculateRowSummary(di);
  }

  onPAYITEMCLIIDChange(event, di: DocItems) {
  //  console.log('*** EditDocumentContentReceiptComponent in onPAYITEMCLIIDChange: ' + JSON.stringify(event));
  //  console.log('*** EditDocumentContentReceiptComponent in onPAYITEMCLIIDChange: ' + JSON.stringify(di));
  }

  checkDiState(event, di: DocItems) {
    //console.log('*** EditDocumentContentReceiptComponent in checkDiState: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentReceiptComponent in checkDiState: ' + JSON.stringify(di));
    if (di.hlt) {
      this.messageService.add({
        severity: 'warn',
        summary: di.PRODCODE + ' информация: ',
        life: 10000,
        detail: ''
          + (di.cnm ? 'Валюта не соответствует установленной в документе! ' : '')
          + (di.rnm ? 'Курс не соответствует установленному в документе! ' : '')
          + (di.cpn ? 'Валютная цена не соответствует расчетной! ' : '')
          + (di.mtc ? 'Не указан УКТЗЕД товара! ' : '')
          + (di.ngs ? 'На момент отгрузки недостаточно товара на складе! ' : '')
          + (di.nls ? 'На момент отгрузки недостаточно товара на складе юридического лица!  ' : '')
          + (di.pdp ? 'Цена отгрузки не соответствует первичному документу' : '')
          + (di.mbc ? 'Не подтверждено соответствие документам передачи на производство! ' : '')
          + (di.mbm ? 'Цена не соответствует перерасчету по документам передачи на производство! ' : '')
      });
    } else {
      this.messageService.add({ severity: 'success', summary: di.PRODCODE + ' информация: OK' });
    }
  }

  onRowSelect(event) {
    console.log('*** EditDocumentContentReceiptComponent in onRowSelect: ' + JSON.stringify(event));
  }

  /** Bulk-updates all rows with the latest vendor income price fetched via `getProdIncomePriceById`. */
  updIncomePrice(event) {
    console.log('*** EditDocumentContentReceiptComponent in updIncomePrice: ' + JSON.stringify(event));

    let doc = this.inputDocument;

    this.diGroupQuery =
        from(this.docItems)
          .pipe(
            concatMap(di => this.prodApi.getProdIncomePriceById(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID, di.DOCITEMQTY, doc.DOCDATE).pipe(
              map(price => {
                di.DOCITEMVPRICE = price[0].PRODINCOMEPRICE;
                di.DOCITEMPRICE = di.DOCITEMVPRICE * doc.DOCRATEVAL;
                di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
                this.calculateRowSummary(di);
              }),
              catchError(this.handleHttpError('getProdIncomePriceById-updIncomePrice'))
            )),

            finalize(() => {
              this.allDocItemsChanged = true;
              this.editing.emit(this.allDocItemsChanged);
            }))
        .subscribe();
  }

  /** Bulk-updates all rows currency and rate fields to match the current document currency. */
  updCurrPrice(event) {
    console.log('*** EditDocumentContentReceiptComponent in updCurrPrice: ' + JSON.stringify(event));


    this.diGroupQuery = from(this.docItems)
      .pipe(
        map(e => {
          e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
          e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
          e.DOCITEMVPRICE = e.DOCITEMPRICE / e.DOCITEMCRATE;
          this.calculateRowSummary(e);
        }),
        finalize(() => {
          this.allDocItemsChanged = true;
          this.editing.emit(this.allDocItemsChanged);
        }))
      .subscribe();

  //  this.diGroupQuery =
  //    from(this.docItems)
  //      .pipe(switchMap(di => of(di)))
  //      .subscribe(e => {
  //        e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
  //        e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
  //        e.DOCITEMVPRICE = e.DOCITEMPRICE / e.DOCITEMCRATE;
  //        this.calculateRowSummary(e);
  //      });

  //  this.allDocItemsChanged = true;
  //  this.editing.emit(this.allDocItemsChanged);
  }

  /** Bulk-updates all rows' retail prices from the linked primary document (`PRIMDOCID`) item prices. */
  updMainPrice(event) {
    console.log('*** EditDocumentContentReceiptComponent in updMainPrice: ' + JSON.stringify(event));
    let parentDocItems: DocItems[];

    this.diGroupQuery =

      this.docApi.getDocItemsByDoc(this.inputDocument.PRIMDOCID)
        .pipe(
          catchError(this.handleHttpError('getDocItemsByDoc-updMainPrice', [] as DocItems[])),
          concatMap(docitems => {
            parentDocItems = docitems;

            console.log('*** EditDocumentContentReceiptComponent in updMainPrice: parentDocItems');
            environment.production || console.table(docitems);

            return from(this.docItems);
          }),
          concatMap(di => {

            console.log('*** EditDocumentContentReceiptComponent in updMainPrice: DocItems loop' + JSON.stringify(di));

            switch (this.inputDocument.DOCTYPE) {

              case 2:
              case 3:
              case 5:
                di.DOCITEMPRICE = round2(di.DOCITEMVPRICE * di.DOCITEMCRATE);
                break;

              case 4:
                let row = parentDocItems.find(r => r.PRODID == di.PRODID);
                if (row) {
                  di.DOCITEMVPRICE = row.DOCITEMPRICE / this.inputDocument.DOCRATEVAL;
                  di.DOCITEMPRICE = row.DOCITEMPRICE;
                  di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
                }
                break;

            }
            this.calculateRowSummary(di);

            return of(di);
          }),

          finalize(() => {
            console.log('*** EditDocumentContentReceiptComponent in updMainPrice: finalize' + JSON.stringify(event));
            this.allDocItemsChanged = true;
            this.editing.emit(this.allDocItemsChanged);
          })
        )
        .subscribe();
  }

  onSaveRows(event) {
    console.log('*** EditDocumentContentReceiptComponent in onSaveRows: ' + JSON.stringify(event));

    this.loading = true;

    this.diGroupQuery =
      from(this.docItems)
        .pipe(concatMap(di => this.docApi.putDocItemById(di)),
          finalize(() => {
            this.loading = false;
            this.calculateDocItemsFields(this.docItems);
            this.allDocItemsChanged = false;
            this.editing.emit(this.allDocItemsChanged);
          }))
        .subscribe();
  }

  onUndoRows(event) {
    console.log('*** EditDocumentContentReceiptComponent in onUndoRows: ' + JSON.stringify(event));

    this.restoreDiCopy();
    this.calculateDocItemsFields(this.docItems);
    this.allDocItemsChanged = false;
    this.editing.emit(this.allDocItemsChanged);
  }

  onRemoveSelection(event) {
    console.log('*** EditDocumentContentPoComponent in onRemoveSelection: ' + JSON.stringify(event));
    this.sDI = [];
  }

  onRemoveAllRow(event) {
    console.log('*** EditDocumentContentReceiptComponent in onRemoveRow: ' + JSON.stringify(event));

    this.deleteAllRows = true;
    this.confirmText = 'DELETE ' + this.docItems.length;
    this.typeDeleteConfirmation = true;
    this.confirmDeleteOn = true;
  }

  onRemoveRow(event) {
    console.log('*** EditDocumentContentReceiptComponent in onRemoveRow: ' + JSON.stringify(event));
    this.deleteAllRows = false;
    this.typeDeleteConfirmation = false;
    this.confirmDeleteOn = true;
  }

  onDeleteConfirmation(event) {
    console.log('*** EditDocumentContentReceiptComponent onDeleteConfirmation: ' + JSON.stringify(event));
    this.confirmDeleteOn = false;

    if (event) {
      console.log('*** EditDocumentContentReceiptComponent onDeleteConfirmation DO DELETE!!! NOW!!!');

      if (this.deleteAllRows) {
        console.log('*** EditDocumentContentReceiptComponent onDeleteConfirmation DO DELETE ALL !!! NOW!!! ********** ALL ********');
        this.docApi.deleteAllDocItemByDocId(this.inputDocument.DOCID)
          .subscribe(() => {
            console.log('*** EditDocumentContentReceiptComponent onDeleteAllItemsConfirmation: DONE!');
            this.sDI = [];
            this.reloadDocContent(null);
          });
      } else {

        from(this.sDI)
          .pipe(
            concatMap(row => this.docApi.deleteDocItemById(row.DOCITEMID)),

            finalize(() => {
              this.sDI = [];
              this.reloadDocContent(null);
            }))
          .subscribe();
      }
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });

      saveAs(
        data,
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
      );
    });
  }

  /*
  DOCID  DOCTYPEID  DOCDATE  DOCEXPDATE  DOCVALDATE  DOCCLIENTSID  DOCPERSONID
  DOCMANCLIID  DOCMANPERID  DOCNUMBER  DOCNOTE1  DOCNOTE2  PRIMDOCID  DOCCLIDEPID
  DOCMANDEPID  DOCCURRID  DOCRATEVAL  DOCACTIVE  DOCFLAG  CLIENTSNAME  DOCLCOCLIID
  LOCALCOMP  PERSONNAME  DOCNAME  DOCSUM  PAYDSUM  SHIPSUM  AUTHOR  CURSTR  LOCALCOMPID
  PAIDJUSTNOW  PAIDNOWDATE  DOCPCOCLIID  PARTNERNAME  PARTNERNAMESTR
  fs	ps	fp	pp	np	go	td	fd
  */

  onExportData() {
    console.log('*** EditDocumentContentReceiptComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.docItems);

      let exportData: any =
        this.docItems
          .filter(e => this.countEmpty || (e.DOCITEMQTY != 0))
          .map(e => ({

          'Код товара': e.PRODCODE,
          'Наименование товара': e.DOCITEMPRODNAME,
          'Количество': e.DOCITEMQTY,
          'Цена с НДС': e.DOCITEMPRICE,
          'Сумма с НДС': e.DOCITEMSUM,
          'Цена в валюте': e.DOCITEMVPRICE,
          'Сумма в валюте': e.DOCITEMVSUM,
          'Цена без НДС': e.DOCITEMPRICEWOVAT,
          'Сумма без НДС': e.DOCITEMSUMWOVAT,
          'Ставка НДС': e.PRODVAT,
          'Таможенный код': e.PRODCUSTCODE,
          'Текст 1': e.DOCITEMTEXT1,
          'Текст 2': e.DOCITEMTEXT2,
          'Отгружено': e.DOCITEMSHIPPED,

          'Валюта': e.CURCODE,
          'Курс': e.DOCITEMCRATE,

          'Входная цена': e.DOCINCOMEPRICE,
          'Доход': e.DOCITEMPROFIT,
          'Сумма дохода': e.DOCITEMPROFITSUM,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  checkCommand(event) {
    this.excelExportItems.forEach(e => e.disabled = this.noItems);
  }

  /** `POST /Product/BatchInfo` rejects more than this many ids in one request. */
  private static readonly BATCH_INFO_MAX_IDS = 2000;

  /**
   * Manufacturer names (`Products.PRODVENDORID` -> `Clients`) for the given rows' products, by PRODID.
   * This component never loads them with its rows (`prodInfo.VENDORNAME` is the default SUPPLIER, a
   * different party), so the export asks for them when it is clicked: one batch call per
   * {@link BATCH_INFO_MAX_IDS} distinct products, merged. The rows are passed in (not read from
   * `this.docItems`) so the lookup and the export describe the same snapshot, and the company /
   * currency / depot arguments are read here, synchronously, at click time.
   * A failed call yields no names for its slice: the export still downloads, with those cells blank.
   */
  private loadManufacturerNames(rows: DocItems[]): Observable<Map<number, string>> {
    const ids = Array.from(new Set(rows.map(e => e.PRODID)));
    if (ids.length === 0) { return of(new Map<number, string>()); }

    const cm = this.inputDocument.DOCMANCLIID;
    const mc = this.globalSettinsData.mainCurrencyId;
    const bc = this.globalSettinsData.balanceCurrencyId;
    const s1 = this.globalSettinsData.mainStockId;
    const s2 = this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0;
    const s3 = this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0;
    const fromDate = new Date();

    return forkJoin(
      chunkArray(ids, EditDocumentContentReceiptComponent.BATCH_INFO_MAX_IDS).map(chunk =>
        this.prodApi.getProdInfoBatch(chunk, cm, mc, bc, s1, s2, s3, fromDate).pipe(
          map(batch => batch.map(r => [r.PRODID, r.PRODVENDORNAME] as [number, string])),
          catchError(err => {
            console.error('*** EditDocumentContentReceiptComponent loadManufacturerNames error: ', err);
            return of([] as [number, string][]);
          })
        )
      )
    ).pipe(map(slices => {
      // No Array.prototype.flat(): the app's tsconfig lib is es2018.
      const names = new Map<number, string>();
      slices.forEach(slice => slice.forEach(([id, name]) => names.set(id, name)));
      return names;
    }));
  }

  async onExportDataWithLogistic() {
    console.log('*** EditDocumentContentComponent onExportData: ')

    // One snapshot of the rows to export, taken before the lookup: the document can be reloaded or
    // edited while the request is in flight, and the file must describe the rows that were clicked.
    const rows = this.docItems.filter(e => this.countEmpty || (e.DOCITEMQTY != 0));
    const manufacturers = await firstValueFrom(this.loadManufacturerNames(rows));

    import("xlsx").then(xlsx => {

      environment.production || console.table(rows);

      let exportData: any =
        rows
          .map(e => ({

          'Код товара': e.PRODCODE,
          'Производитель': manufacturers.get(e.PRODID),
          'Поставщик': e.prodInfo?.VENDORNAME,   // default supplier (ProdVendors), not the manufacturer
          'Наименование товара': e.DOCITEMPRODNAME,
          'Количество': e.DOCITEMQTY,
          'Цена с НДС': e.DOCITEMPRICE,
          'Сумма с НДС': e.DOCITEMSUM,
          'Цена в валюте': e.DOCITEMVPRICE,
          'Сумма в валюте': e.DOCITEMVSUM,
          'Цена без НДС': e.DOCITEMPRICEWOVAT,
          'Сумма без НДС': e.DOCITEMSUMWOVAT,
          'Ставка НДС': e.PRODVAT,
          'Таможенный код': e.PRODCUSTCODE,
          'Текст 1': e.DOCITEMTEXT1,
          'Текст 2': e.DOCITEMTEXT2,

          'Отгружено': e.DOCITEMSHIPPED,

          'На складе': e.prodInfo.PRODSTOCK,
          'В резерве': e.prodInfo.PRODRESERVE,
          'Продано': e.prodInfo.PRODSOLD,
          'В пути': e.prodInfo.PRODONWAY,

          'Валюта': e.CURCODE,
          'Курс': e.DOCITEMCRATE,

          'Розница': e.DOCITEMRETPRICE,
          'Розница в валюте': e.DOCITEMRETVPRICE,
          'Скидка': e.DISCOUNT,

          'Расчетная $': e.U4ETKA,
          'Сумма расчет$': e.DOCITEMSUMU4,

          'Входная цена': e.DOCINCOMEPRICE,
          'Доход': e.DOCITEMPROFIT,
          'Сумма дохода': e.DOCITEMPROFITSUM,

          'Цена поставщика': e.prodInfo.VENDORPRICE,
          'Вес': e.prodInfo.PRODWEIGHT,
          'Доставка': e.prodInfo.VENADDCONST,
          'Пошлина': e.prodInfo.VENMULCONST,
          'Дополнительно +': e.prodInfo.VENADDVAR,
          'Дополнительно *': e.prodInfo.VENMULVAR,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  onStateSave(state) {
    console.log('*** EditDocumentContentReceiptComponent onStateSave: ' + JSON.stringify(state));
  }

  onStateRestore(state) {
    console.log('*** EditDocumentContentReceiptComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        this.cf3[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }
  }

  cleanSortMode(state) {
    console.log('*** EditDocumentContentReceiptComponent cleanSortMode: ' + JSON.stringify(state));

    this.docItemTable.reset();
    this.reloadDocContent(state);
  }

  updateRowIndexes(state) {
    console.log('*** EditDocumentContentReceiptComponent updateRowIndexes: ' + JSON.stringify(state));

    let rowIndex = 10;

    this.diSaveQuery =
      concat(
        from(this.docItems)
          .pipe(concatMap(di => {
            di.PAYITEMCLIID = rowIndex;
            rowIndex += 10;

            return this.docApi.putDocItemById(di);
          })),

        of(false).pipe(map(data => this.loading = data))
      )
        .subscribe();
  }

  countEmptyModeChange(event) {
    console.log('*** EditDocumentContentComponent countEmptyModeChange: ' + JSON.stringify(event));

    this.countEmpty = event.checked;

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
      this.countEmptyChange.emit(this.countEmpty);
    }
  }
}
